import { useEffect, useState } from "react";
import type { CountryKey, DateParts } from "@/lib/dateIntent";
import { buildJumpPills } from "@/lib/keywordMesh";

/**
 * Sticky pill strip rendered under TodayDateBlock. All pills wrap on mobile
 * (no horizontal swipe) so users never miss deep-link chips. Active highlight
 * via IntersectionObserver — no library.
 */
export function JumpLinks({ country, date }: { country: CountryKey; date: DateParts }) {
  const pills = buildJumpPills(country, date);
  const [active, setActive] = useState<string | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const ids = pills.map((p) => p.anchor);
    const els = ids
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => !!el);
    if (els.length === 0) return;
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setActive(e.target.id);
            break;
          }
        }
      },
      { threshold: 0.5 },
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [pills]);

  return (
    <nav
      aria-label="Jump to dated sections"
      className="sticky top-16 z-30 mx-auto max-w-6xl px-4 py-3 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 border-y border-border"
    >
      <div className="flex flex-wrap gap-2">
        {pills.map((p) => (
          <a
            key={p.id}
            href={`#${p.anchor}`}
            data-active={active === p.anchor ? "true" : undefined}
            className="rounded-full border border-border px-3 py-1.5 text-xs font-semibold whitespace-nowrap data-[active=true]:bg-primary data-[active=true]:text-primary-foreground hover:bg-muted"
          >
            <span data-nosnippet>{p.label}</span>
          </a>
        ))}
      </div>
    </nav>
  );
}